import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/apiClient";
import { QuestionView } from "@/components/QuestionView";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Timer, ArrowRight, PlayCircle } from "lucide-react";

const PLAN = [
  { label: "Ragionamento Verbale", n: 20, min: 35 },
  { label: "Ragionamento Numerico", n: 10, min: 20 },
  { label: "Ragionamento Astratto", n: 10, min: 10 },
];

export default function Simulation() {
  const nav = useNavigate();
  const [phase, setPhase] = useState("intro");
  const [sim, setSim] = useState(null);
  const [sec, setSec] = useState(0);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [remaining, setRemaining] = useState(0);
  const submitting = useRef(false);

  const start = async () => {
    try {
      const { data } = await api.post("/simulation/start");
      setSim(data); setPhase("running"); setSec(0); setIdx(0);
      setRemaining(data.sections[0].minutes * 60);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const finishAll = async () => {
    if (submitting.current) return;
    submitting.current = true;
    const all = [];
    sim.sections.forEach((s) => s.questions.forEach((q) => all.push({ question_id: q.id, answer: answers[q.id] || null })));
    try {
      await api.post(`/simulation/${sim.attempt_id}/submit`, { answers: all });
      nav(`/simulazione/${sim.attempt_id}/risultati`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); submitting.current = false; }
  };

  const nextSection = () => {
    if (sec < sim.sections.length - 1) {
      const ns = sec + 1;
      setSec(ns); setIdx(0); setRemaining(sim.sections[ns].minutes * 60);
    } else { finishAll(); }
  };

  useEffect(() => {
    if (phase !== "running" || remaining <= 0) return;
    const t = setInterval(() => setRemaining((r) => r - 1), 1000);
    return () => clearInterval(t);
  }, [phase, remaining, sec]);

  useEffect(() => {
    if (phase === "running" && remaining === 0) nextSection();
    // eslint-disable-next-line
  }, [remaining]);

  if (phase === "intro") {
    return (
      <Layout>
        <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
          <div className="label-mono text-[#002FA7] mb-4">— Simulazione completa</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-6">Simulazione ufficiale in condizioni reali</h1>
          <p className="text-lg text-slate-600 max-w-2xl leading-relaxed mb-8">
            Tre sezioni cronometrate, esattamente come all'esame. Una volta avviata, ogni sezione ha il suo tempo:
            al termine ricevi il punteggio dettagliato con la spiegazione di ogni risposta.
          </p>
          <div className="border border-slate-200 bg-white divide-y divide-slate-200 mb-10">
            {PLAN.map((p, i) => (
              <div key={i} className="flex items-center justify-between p-5">
                <div className="font-display font-bold">{i + 1}. {p.label}</div>
                <div className="label-mono text-slate-500 font-mono-num">{p.n} domande · {p.min} min</div>
              </div>
            ))}
          </div>
          <Button data-testid="sim-start" onClick={start} className="rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono py-6 px-8 shadow-hard-black">
            <PlayCircle className="w-5 h-5 mr-2" />Avvia la simulazione
          </Button>
        </section>
      </Layout>
    );
  }

  const section = sim.sections[sec];
  const q = section.questions[idx];
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const answered = section.questions.filter((qq) => answers[qq.id]).length;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
        <div className="border border-slate-200 bg-white flex items-center justify-between p-4 mb-6">
          <div>
            <div className="label-mono text-slate-500">Sezione {sec + 1}/3 · {section.title}</div>
            <div className="font-display font-bold text-lg">Domanda {idx + 1} di {section.questions.length}</div>
          </div>
          <div className="flex items-center gap-6">
            <div className="label-mono text-slate-500">Risposte: <span className="font-mono-num text-slate-900">{answered}/{section.questions.length}</span></div>
            <div className={`flex items-center gap-2 px-4 py-2 border ${remaining < 60 ? "border-red-500 text-red-600" : "border-slate-300"}`}>
              <Timer className="w-4 h-4" /><span className="font-mono-num text-lg font-bold" data-testid="sim-timer">{mm}:{ss}</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 border border-slate-200 bg-white p-8">
            <QuestionView q={q} value={answers[q.id]} onChange={(k) => setAnswers({ ...answers, [q.id]: k })} />
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
              <Button variant="outline" className="rounded-none label-mono" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="sim-prev">Precedente</Button>
              {idx < section.questions.length - 1
                ? <Button className="rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono" onClick={() => setIdx(idx + 1)} data-testid="sim-next">Successiva</Button>
                : <Button className="rounded-none bg-[#0A0F1A] hover:bg-slate-800 text-white label-mono" onClick={nextSection} data-testid="sim-next-section">
                    {sec < sim.sections.length - 1 ? "Sezione successiva" : "Termina simulazione"} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>}
            </div>
          </div>
          <div className="lg:col-span-1 border border-slate-200 bg-white p-4 h-fit sticky top-20">
            <div className="label-mono text-slate-500 mb-3">Sezione {sec + 1}</div>
            <div className="grid grid-cols-5 gap-2">
              {section.questions.map((qq, i) => (
                <button key={qq.id} onClick={() => setIdx(i)} data-testid={`sim-goto-${i}`}
                  className={`aspect-square text-sm font-mono-num border ${i === idx ? "border-[#002FA7] bg-[#002FA7] text-white" : answers[qq.id] ? "border-[#002FA7] text-[#002FA7]" : "border-slate-200 text-slate-500"}`}>{i + 1}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
